import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowLeft,
  Download,
  Building2
} from 'lucide-react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { receptoresApi } from '../../utils/api';
import { provincias, cantones, distritos } from '../../utils/costaRicaLocations';

// Fixed column names for the template
const COLUMNS = [
  { key: 'identificacion', label: 'Identificacion' },
  { key: 'nombre', label: 'Nombre' },
  { key: 'email', label: 'Email' },
  { key: 'telefono', label: 'Telefono' },
  { key: 'provincia', label: 'Provincia' },
  { key: 'canton', label: 'Canton' },
  { key: 'distrito', label: 'Distrito' },
  { key: 'direccion', label: 'Direccion' },
  { key: 'moneda', label: 'Moneda' },
  { key: 'condicion', label: 'Condicion' },
  { key: 'plazo', label: 'Plazo' },
];

// Build reverse lookup maps: name → code
const norm = (s) => String(s || '').toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const PROVINCIA_MAP = {};
for (const [code, name] of Object.entries(provincias)) {
  PROVINCIA_MAP[norm(name)] = code;
}

// cantones: { "101": "San José", "407": "Belén", ... } → lookup by province + name
const CANTON_LOOKUP = {}; // "provCode:normalizedName" → cantonCode (2 digits)
for (const [fullCode, name] of Object.entries(cantones)) {
  const provCode = fullCode[0];
  const cantonCode = fullCode.slice(1);
  CANTON_LOOKUP[`${provCode}:${norm(name)}`] = cantonCode;
}

// distritos: same pattern
const DISTRITO_LOOKUP = {};
if (distritos) {
  for (const [fullCode, name] of Object.entries(distritos)) {
    const provCantonCode = fullCode.slice(0, 3);
    const distritoCode = fullCode.slice(3);
    DISTRITO_LOOKUP[`${provCantonCode}:${norm(name)}`] = distritoCode;
  }
}

export default function ImportarClientes() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [previewMode, setPreviewMode] = useState(true);
  const [progress, setProgress] = useState({ current: 0, total: 0, currentName: '' });

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      COLUMNS.map(c => c.label),
      ['3101123456', 'Empresa Ejemplo S.A.', 'info@ejemplo.com', '88881234', '1', '01', '01', 'Barrio Los Yoses, 200m norte', 'USD', 'Credito', 30],
      ['155812345678', 'Juan Pérez', 'juan@mail.com', '70001234', '4', '07', '01', 'San Antonio de Belén', 'CRC', 'Contado', 0],
    ]);
    // Set column widths
    ws['!cols'] = [{ wch: 16 }, { wch: 30 }, { wch: 28 }, { wch: 12 }, { wch: 10 }, { wch: 8 }, { wch: 8 }, { wch: 35 }, { wch: 8 }, { wch: 10 }, { wch: 8 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Clientes');
    XLSX.writeFile(wb, 'plantilla_clientes.xlsx');
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExt = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));

    if (!validExtensions.includes(fileExt)) {
      toast.error('Por favor seleccione un archivo Excel (.xlsx, .xls) o CSV');
      return;
    }

    setFile(selectedFile);
    parseFile(selectedFile);
  };

  const normalize = (str) => String(str || '').toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const parseFile = (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

        if (jsonData.length < 2) {
          toast.error('El archivo no contiene datos');
          return;
        }

        // Normalize headers
        const headers = jsonData[0].map(h => normalize(h));

        // Map columns by matching normalized names
        const colMap = {};
        for (const col of COLUMNS) {
          const normalizedLabel = normalize(col.label);
          const idx = headers.findIndex(h => h === normalizedLabel || h.includes(normalizedLabel));
          if (idx >= 0) {
            colMap[col.key] = idx;
          }
        }

        // Fallback aliases for common variations
        const aliases = {
          identificacion: ['cedula', 'cedula juridica', 'id', 'nit', 'ruc'],
          nombre: ['cliente', 'razon social', 'name'],
          email: ['correo', 'mail', 'facturacion electronica'],
          telefono: ['phone', 'tel', 'telefono 1'],
          canton: ['cantón'],
          distrito: ['ciudad'],
          direccion: ['otras senas', 'address'],
          moneda: ['currency', 'divisa'],
          condicion: ['condicion venta', 'terminos', 'terminos de pago'],
          plazo: ['plazo credito', 'dias credito', 'dias'],
        };

        for (const [key, aliasList] of Object.entries(aliases)) {
          if (colMap[key] !== undefined) continue;
          for (const alias of aliasList) {
            const idx = headers.findIndex(h => h.includes(alias));
            if (idx >= 0) {
              colMap[key] = idx;
              break;
            }
          }
        }

        const getVal = (row, key) => colMap[key] !== undefined ? String(row[colMap[key]] || '').trim() : '';

        // Parse rows
        const parsedClientes = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || row.length === 0) continue;

          const identificacion = getVal(row, 'identificacion').replace(/[-\s]/g, '');
          if (!identificacion || identificacion === '0000000000') continue;

          const nombre = getVal(row, 'nombre');
          if (!nombre && !identificacion) continue;

          // Parse moneda
          const monedaRaw = getVal(row, 'moneda').toUpperCase();
          const moneda = monedaRaw === 'USD' || monedaRaw === 'DOLAR' || monedaRaw === 'DOLARES' ? 'USD' : 'CRC';

          // Parse condicion de venta
          const condicionRaw = getVal(row, 'condicion').toLowerCase();
          let condicionVenta = '01'; // Contado por defecto
          if (condicionRaw.includes('credito') || condicionRaw.includes('crédito') || condicionRaw.includes('neto')) {
            condicionVenta = '02';
          }

          // Parse plazo
          const plazoRaw = getVal(row, 'plazo');
          let diasCredito = parseInt(plazoRaw) || 0;
          if (condicionVenta === '02' && diasCredito === 0) diasCredito = 30; // Default 30 días si es crédito

          // Parse provincia (accept code "1" or name "San José")
          const provinciaRaw = getVal(row, 'provincia');
          const provincia = /^\d$/.test(provinciaRaw) ? provinciaRaw : (PROVINCIA_MAP[norm(provinciaRaw)] || '');

          // Parse canton (accept code "01" or name "Belén")
          const cantonRaw = getVal(row, 'canton');
          let canton = '';
          if (/^\d{1,2}$/.test(cantonRaw)) {
            canton = String(cantonRaw).padStart(2, '0');
          } else if (cantonRaw && provincia) {
            canton = CANTON_LOOKUP[`${provincia}:${norm(cantonRaw)}`] || '';
          }

          // Parse distrito (accept code "01" or name "San Antonio")
          const distritoRaw = getVal(row, 'distrito');
          let distrito = '';
          if (/^\d{1,2}$/.test(distritoRaw)) {
            distrito = String(distritoRaw).padStart(2, '0');
          } else if (distritoRaw && provincia && canton) {
            distrito = DISTRITO_LOOKUP[`${provincia}${canton}:${norm(distritoRaw)}`] || '';
          }

          // Clean phone
          let telefono = getVal(row, 'telefono').replace(/[^\d]/g, '').replace(/^506/, '');

          // Clean direccion
          let direccion = getVal(row, 'direccion').split('\r\n')[0].split('\n')[0].replace(/^Direcci[oó]n:\s*/i, '');

          parsedClientes.push({
            nombre: nombre || identificacion,
            identificacion,
            telefono,
            email: getVal(row, 'email').split(';')[0].trim(),
            provincia,
            canton,
            distrito,
            direccion,
            moneda,
            condicionVenta,
            diasCredito,
          });
        }

        setClientes(parsedClientes);
        setPreviewMode(true);
        setResults(null);

        if (parsedClientes.length === 0) {
          toast.error('No se encontraron clientes. Verifique que las columnas coincidan con la plantilla.');
        } else {
          toast.success(`${parsedClientes.length} clientes encontrados`);
        }
      } catch (error) {
        console.error('Error parsing file:', error);
        toast.error('Error al leer el archivo');
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    if (clientes.length === 0) {
      toast.error('No hay clientes para importar');
      return;
    }

    setImporting(true);
    setPreviewMode(false);
    setProgress({ current: 0, total: clientes.length, currentName: '' });

    const totalResults = { imported: 0, updated: 0, errors: [] };

    try {
      for (let i = 0; i < clientes.length; i++) {
        const cliente = clientes[i];

        setProgress({
          current: i + 1,
          total: clientes.length,
          currentName: cliente.nombre || cliente.identificacion || ''
        });

        try {
          const response = await receptoresApi.import([cliente]);
          const result = response.data?.data || response.data;

          totalResults.imported += result.imported || 0;
          totalResults.updated += result.updated || 0;
          if (result.errors?.length > 0) {
            totalResults.errors.push(...result.errors);
          }
        } catch (err) {
          totalResults.errors.push({ identificacion: cliente.identificacion, error: err.message });
        }
      }

      setProgress({ current: clientes.length, total: clientes.length, currentName: 'Completado' });
      setResults(totalResults);
      toast.success('Importación completada');
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Error al importar clientes');
      setResults({ ...totalResults, errors: [...totalResults.errors, { identificacion: 'General', error: error.message }] });
    } finally {
      setImporting(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
      parseFile(droppedFile);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/clientes')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Importar Clientes</h1>
            <p className="text-sm text-gray-500">Importe clientes desde un archivo Excel o CSV</p>
          </div>
        </div>
        <button
          onClick={downloadTemplate}
          className="flex items-center gap-2 px-4 py-2 border-2 border-green-500 text-green-600 rounded-lg hover:bg-green-50 font-medium text-sm"
        >
          <Download className="h-4 w-4" />
          Descargar Plantilla
        </button>
      </div>

      {/* Upload Zone */}
      {!file && (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-blue-400 hover:bg-blue-50/50 transition-colors cursor-pointer"
        >
          <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Arrastre un archivo aquí
          </h3>
          <p className="text-gray-500 mb-4">o haga clic para seleccionar</p>
          <p className="text-sm text-gray-400">
            Formatos soportados: .xlsx, .xls, .csv
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}

      {/* File Selected */}
      {file && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* File Info */}
          <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="h-8 w-8 text-green-600" />
              <div>
                <p className="font-medium text-gray-900">{file.name}</p>
                <p className="text-sm text-gray-500">
                  {clientes.length} clientes encontrados
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setFile(null);
                setClientes([]);
                setResults(null);
              }}
              className="text-gray-500 hover:text-red-600 text-sm"
            >
              Cambiar archivo
            </button>
          </div>

          {/* Progress */}
          {importing && (
            <div className="p-6 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4">Importando clientes...</h3>
              <div className="mb-2 flex justify-between text-sm">
                <span className="text-gray-600">Progreso: {progress.current} de {progress.total}</span>
                <span className="text-gray-900 font-medium">
                  {Math.round((progress.current / progress.total) * 100)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
              <p className="text-sm text-gray-500 mt-2 truncate">
                Procesando: <span className="font-medium">{progress.currentName}</span>
              </p>
            </div>
          )}

          {/* Results */}
          {results && !importing && (
            <div className="p-6 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4">Resultados de la importación</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-700">{results.imported || 0}</p>
                  <p className="text-sm text-green-600">Nuevos</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <AlertCircle className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-700">{results.updated || 0}</p>
                  <p className="text-sm text-blue-600">Actualizados</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <XCircle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-red-700">{results.errors?.length || 0}</p>
                  <p className="text-sm text-red-600">Errores</p>
                </div>
              </div>

              {results.errors && results.errors.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg max-h-40 overflow-y-auto">
                  <p className="text-sm font-medium text-red-700 mb-2">Errores:</p>
                  <ul className="text-sm text-red-600 list-disc list-inside">
                    {results.errors.slice(0, 20).map((err, i) => (
                      <li key={i}>{err.identificacion}: {err.error}</li>
                    ))}
                    {results.errors.length > 20 && (
                      <li>... y {results.errors.length - 20} más</li>
                    )}
                  </ul>
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => navigate('/clientes')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Ver Clientes
                </button>
              </div>
            </div>
          )}

          {/* Preview Table */}
          {previewMode && clientes.length > 0 && (
            <>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">#</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Identificación</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Nombre</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Email</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Teléfono</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Prov</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Cantón</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Distrito</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Moneda</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Condición</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Plazo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {clientes.slice(0, 50).map((c, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-500">{idx + 1}</td>
                        <td className="px-4 py-2 font-mono text-xs">{c.identificacion}</td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span className="truncate max-w-[200px]">{c.nombre}</span>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-gray-600 truncate max-w-[180px]">{c.email || '-'}</td>
                        <td className="px-4 py-2 text-gray-600">{c.telefono || '-'}</td>
                        <td className="px-4 py-2 text-gray-600">{c.provincia || '-'}</td>
                        <td className="px-4 py-2 text-gray-600">{c.canton || '-'}</td>
                        <td className="px-4 py-2 text-gray-600">{c.distrito || '-'}</td>
                        <td className="px-4 py-2 text-gray-600">{c.moneda}</td>
                        <td className="px-4 py-2 text-gray-600">{c.condicionVenta === '02' ? 'Crédito' : 'Contado'}</td>
                        <td className="px-4 py-2 text-gray-600">{c.diasCredito > 0 ? `${c.diasCredito}d` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {clientes.length > 50 && (
                  <div className="p-3 bg-gray-50 text-center text-sm text-gray-500">
                    Mostrando 50 de {clientes.length} clientes
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  <p><strong>{clientes.length}</strong> clientes a importar</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setFile(null);
                      setClientes([]);
                    }}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={importing}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Importar {clientes.length} Clientes
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Column Reference */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-blue-900">Columnas del archivo</h4>
          <span className="text-xs text-blue-500">Use la plantilla para el formato correcto</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-blue-700">
                <th className="pb-1 pr-4 font-medium">Columna</th>
                <th className="pb-1 pr-4 font-medium">Requerido</th>
                <th className="pb-1 font-medium">Ejemplo</th>
              </tr>
            </thead>
            <tbody className="text-blue-600">
              <tr><td className="py-0.5 pr-4 font-semibold">Identificacion</td><td className="pr-4">Sí</td><td>3101123456</td></tr>
              <tr><td className="py-0.5 pr-4 font-semibold">Nombre</td><td className="pr-4">Sí</td><td>Empresa Ejemplo S.A.</td></tr>
              <tr><td className="py-0.5 pr-4 font-semibold">Email</td><td className="pr-4">No</td><td>info@ejemplo.com</td></tr>
              <tr><td className="py-0.5 pr-4 font-semibold">Telefono</td><td className="pr-4">No</td><td>88881234</td></tr>
              <tr><td className="py-0.5 pr-4 font-semibold">Provincia</td><td className="pr-4">No</td><td>1=San José, 2=Alajuela, 3=Cartago, 4=Heredia, 5=Guanacaste, 6=Puntarenas, 7=Limón</td></tr>
              <tr><td className="py-0.5 pr-4 font-semibold">Canton</td><td className="pr-4">No</td><td>Código de 2 dígitos (ej: 01, 07)</td></tr>
              <tr><td className="py-0.5 pr-4 font-semibold">Distrito</td><td className="pr-4">No</td><td>Código de 2 dígitos (ej: 01, 03)</td></tr>
              <tr><td className="py-0.5 pr-4 font-semibold">Direccion</td><td className="pr-4">No</td><td>Barrio Los Yoses, 200m norte</td></tr>
              <tr><td className="py-0.5 pr-4 font-semibold">Moneda</td><td className="pr-4">No</td><td>USD o CRC (defecto: CRC)</td></tr>
              <tr><td className="py-0.5 pr-4 font-semibold">Condicion</td><td className="pr-4">No</td><td>Contado o Credito (defecto: Contado)</td></tr>
              <tr><td className="py-0.5 pr-4 font-semibold">Plazo</td><td className="pr-4">No</td><td>15, 30, 60, 90 (días de crédito)</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
